/**
 * MARK:- Volume savings (#2915)
 * One savings contract for the volume bundle builder. Pure, no DOM; the fixture table in
 * qa/volume-savings-fixtures.mjs runs against this file.
 *
 * Rounding matches Discount Kit (live test, 2026-10-06): the discount is taken per unit,
 * floor(price_cents * pct / 100), then multiplied by quantity. Prices are integer cents.
 * Tiers are ascending [{ qty, pct }] from custom-volume-discount-data.
 *
 * window.NHVolumeSavings in the browser; module.exports in node.
 */
(function (root) {
  'use strict';

  // MARK:- #2920 Loaded by theme.liquid and again by section tags; the first copy owns the page (one sentryState)
  if (root.NHVolumeSavings && typeof module === 'undefined') return;

  // MARK:- Free-shipping register A. The Shopify automatic discount "Free Shipping $199+" keys on
  // the post-discount total, so the suffix needs the caller's estimated post-discount total (cents)
  // to clear FREE_SHIP_MIN; an unknown total (null/undefined) drops the promise.
  const FREE_SHIP_SUFFIX = ' + free shipping';
  const FREE_SHIP_MIN = 19900;

  function unitDiscount(price, pct) {
    return Math.floor((price * pct) / 100);
  }

  function boxSize(tiers) {
    return tiers.length ? tiers[0].qty : 0;
  }

  // MARK:- Box copy only when every tier is a whole number of boxes (4/8/12 with a box of 4)
  function boxAligned(tiers) {
    const box = boxSize(tiers);
    return box > 0 && tiers.every((tier) => tier.qty % box === 0);
  }

  function currentTier(tiers, n) {
    let current = null;
    tiers.forEach((tier) => {
      if (n >= tier.qty) current = tier;
    });
    return current;
  }

  function nextTier(tiers, n) {
    return tiers.find((tier) => n < tier.qty) || null;
  }

  function ordinalBox(k) {
    if (k <= 1) return 'box';
    if (k === 2) return '2nd box';
    if (k === 3) return '3rd box';
    return `${k}th box`;
  }

  function suffixFor(tiers, tier, total) {
    return tier && tier === tiers[tiers.length - 1] && total != null && total >= FREE_SHIP_MIN ? FREE_SHIP_SUFFIX : '';
  }

  // MARK:- Headline: steer to the next full box; past the top tier, steer to filling the open box.
  // total = estimated post-discount cents at the tier the headline names (omit when unknown)
  function nextCopy(tiers, n, nouns, total) {
    if (!tiers.length) return '';
    const one = (nouns && nouns.one) || 'plant';
    const other = (nouns && nouns.other) || 'plants';
    const next = nextTier(tiers, n);
    const box = boxSize(tiers);
    const aligned = boxAligned(tiers);

    if (next) {
      const remaining = next.qty - n;
      if (!aligned) return `Add ${remaining} more ${remaining === 1 ? one : other} to save ${next.pct}%`;
      return `Add ${remaining} more to fill your ${ordinalBox(next.qty / box)} and save ${next.pct}%${suffixFor(tiers, next, total)}`;
    }

    const top = tiers[tiers.length - 1];
    const unlocked = `${top.pct}% off${suffixFor(tiers, top, total)} unlocked`;
    if (!aligned || n % box === 0) return unlocked;
    return `Add ${box - (n % box)} more to fill your ${ordinalBox(Math.ceil(n / box))} (${unlocked})`;
  }

  // MARK:- Discount on the whole next tier: counted lines at their own prices, the plants still
  // to add at fillPrice (cheapest grid card, else cheapest counted line). Null when nothing to say.
  function estimateNextTier(tiers, n, lines, fillPrice) {
    const next = nextTier(tiers, n);
    if (!next) return null;
    let cheapest = null;
    let cents = 0;
    (lines || []).forEach((line) => {
      cents += line.qty * unitDiscount(line.price, next.pct);
      if (cheapest == null || line.price < cheapest) cheapest = line.price;
    });
    const fill = fillPrice ?? cheapest;
    if (fill == null) return null;
    return cents + (next.qty - n) * unitDiscount(fill, next.pct);
  }

  // MARK:- Savings row: the cart's own applied discount when nothing is staged (exact); with
  // staged items (or cartSaved unknown, null) an estimate at the current tier across both
  function savingsRow(tiers, cartSaved, cartLines, staged) {
    const all = (cartLines || []).concat(staged || []);
    const stagedQty = (staged || []).reduce((sum, line) => sum + line.qty, 0);
    if (stagedQty === 0 && cartSaved != null) return { cents: cartSaved, exact: true };
    const n = all.reduce((sum, line) => sum + line.qty, 0);
    const current = currentTier(tiers, n);
    let cents = 0;
    if (current) {
      all.forEach((line) => {
        cents += line.qty * unitDiscount(line.price, current.pct);
      });
    }
    return { cents, exact: false };
  }

  // MARK:- Default money format for node fixtures; the bundler passes its shop-format formatter
  function formatCents(cents) {
    return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  // MARK:- Panel savings callout once a tier is reached. No "about": the per-unit floor rule equals
  // the cart to the cent. Null below the first tier or with nothing saved.
  function savingsCallout(tiers, n, cents, format) {
    const current = currentTier(tiers, n);
    if (!current || !(cents > 0)) return null;
    const fmt = format || formatCents;
    return { amount: `You save ${fmt(cents)}`, pct: `(${current.pct}% off)` };
  }

  // MARK:- Mobile peek strip at a reached tier; null below it (the caller keeps its add-N-more strip).
  // total = post-discount cents, gates the free-ship suffix the same way nextCopy does
  function peekCopy(tiers, n, cents, total, format) {
    const current = currentTier(tiers, n);
    if (!current || !(cents > 0)) return null;
    const fmt = format || formatCents;
    const next = nextTier(tiers, n);
    if (next) return `You save ${fmt(cents)} · add ${next.qty - n} more for ${next.pct}%`;
    return `You save ${fmt(cents)} (${current.pct}% off)${suffixFor(tiers, current, total)}`;
  }

  // MARK:- Shipping restrictions (custom.restricted_shipping_states, the PDP block's field)
  function parseStates(value) {
    return String(value || '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
  }

  function isRestricted(states, state) {
    if (!state || !states || !states.length) return false;
    return states.includes(String(state).toUpperCase());
  }

  // MARK:- Shopper state, the builder's order: a zone event's stateCode, else localStorage growing_state
  // (saved), else (no event only) the Liquid fallback (cart attribute customer_state, then a US default
  // address). A zone event without a state skips the fallback, which may predate the new ZIP. Null = unknown.
  function pickShipState(detail, saved, fallback) {
    const state = detail ? detail.stateCode || saved : saved || fallback;
    return state ? String(state).trim().toUpperCase() : null;
  }

  function savedShipState() {
    try {
      return root.localStorage.getItem('growing_state');
    } catch {
      return null; // storage blocked, or node
    }
  }

  // MARK:- Cart suggestions (#2915): which candidates show, in order. Restricted for the state -> hidden;
  // then the first max (0/absent = no cap) of the rest show. lists: one string[] per candidate
  function suggestionsShown(lists, state, max) {
    let count = 0;
    return lists.map((states) => {
      if (isRestricted(states, state) || (max > 0 && count >= max)) return false;
      count += 1;
      return true;
    });
  }

  function restrictedCopy(state) {
    return `Can't ship to ${String(state).toUpperCase()}`;
  }

  function restrictedAlert(count, state) {
    const what = count === 1 ? 'the plant' : `the ${count} plants`;
    return `Remove ${what} that can't ship to ${String(state).toUpperCase()} to add your bundle.`;
  }

  // MARK:- HTML entity decode. The browser path is a detached <textarea> (RCDATA: decodes every
  // named entity, never builds elements); node (fixtures) falls back to numeric + a short named table
  const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', trade: '™', reg: '®', copy: '©' };
  function decodeEntities(value) {
    const str = String(value ?? '');
    if (!str.includes('&')) return str;
    if (typeof document !== 'undefined' && document.createElement) {
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }
    return str.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, ent) => {
      if (ent[0] === '#') {
        const code = ent[1] === 'x' || ent[1] === 'X' ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
        return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
      }
      return NAMED_ENTITIES[ent.toLowerCase()] ?? whole;
    });
  }

  // MARK:- Title key for Plant Sentry matching: decoded, whitespace (incl. nbsp) collapsed; compared with ===
  function titleKey(value) {
    return decodeEntities(value).replace(/\s+/g, ' ').trim();
  }

  // MARK:- Plant Sentry cart validation 422 (live 2026-10-06, /cart/add.js): one error, plain text, in both
  // message and description: "<title> is not allowed to be shipped to <ST>"
  function parseShipError(description) {
    const match = /^(.+) is not allowed to be shipped to ([A-Z]{2})$/.exec(titleKey(description));
    return match ? { title: titleKey(match[1]), state: match[2] } : null;
  }

  // MARK:- Staged match for a block: same title AND a known restricted list naming the state; a staged
  // item without a list (legacy session entry) is a fallback only. A known list without the state never matches.
  // entries: [[variantId, { title, restricted }]]
  function recoveryStagedIds(entries, blocked) {
    const strong = [];
    const weak = [];
    entries.forEach(([id, meta]) => {
      if (titleKey(meta.title) !== blocked.title) return;
      if (!Array.isArray(meta.restricted)) weak.push(id);
      else if (isRestricted(meta.restricted, blocked.state)) strong.push(id);
    });
    return strong.length ? strong : weak;
  }

  // MARK:- Cart match for a block: lines with the title, minus products whose known list (by variant, from
  // rendered cards or staged meta) excludes the state. Exactly one product left -> its line keys; else [] (fail safe).
  // statesByVariant: Map(variantId -> string[])
  function recoveryCartKeys(items, statesByVariant, blocked) {
    const named = (items || []).filter((item) => titleKey(item.product_title) === blocked.title);
    const known = new Map();
    named.forEach((item) => {
      const states = statesByVariant.get(Number(item.variant_id));
      if (states) known.set(item.product_id, states);
    });
    const left = named.filter((item) => !known.has(item.product_id) || isRestricted(known.get(item.product_id), blocked.state));
    const products = new Set(left.map((item) => item.product_id));
    return products.size === 1 ? left.map((item) => item.key) : [];
  }

  // MARK:- One-click recovery offer: kind 'staged' (unstage), 'cart' (remove the cart line, re-add the bundle)
  // or 'add' (#2920: any other add form; remove the cart line, retry the add)
  function recoveryCopy(kind, title, state) {
    if (kind === 'staged') return { text: `${title} can't ship to your ${state} delivery address.`, button: 'Remove it from my bundle' };
    const text = `${title} in your cart can't ship to your ${state} delivery address.`;
    return { text, button: kind === 'add' ? 'Remove it and continue' : 'Remove it and add my bundle' };
  }

  // MARK:- #2920 one state source: the state a Plant Sentry 422 named (it judges the cart's delivery address).
  // Page-scoped like the builder's old sentryState (no localStorage / cart attribute write); a zone change clears it.
  // Readers put it ahead of pickShipState. setSentryState fires nh:sentry-state on document so they re-judge.
  let sentryState = null;
  function sentryShipState() {
    return sentryState;
  }
  function setSentryState(state) {
    const next = state ? String(state).trim().toUpperCase() : null;
    if (next === sentryState) return;
    sentryState = next;
    if (typeof document !== 'undefined') document.dispatchEvent(new CustomEvent('nh:sentry-state', { detail: { state: next } }));
  }

  const NHVolumeSavings = {
    FREE_SHIP_SUFFIX,
    unitDiscount,
    boxSize,
    boxAligned,
    currentTier,
    nextTier,
    ordinalBox,
    nextCopy,
    estimateNextTier,
    savingsRow,
    formatCents,
    savingsCallout,
    peekCopy,
    parseStates,
    isRestricted,
    pickShipState,
    savedShipState,
    suggestionsShown,
    restrictedCopy,
    restrictedAlert,
    decodeEntities,
    titleKey,
    parseShipError,
    recoveryStagedIds,
    recoveryCartKeys,
    recoveryCopy,
    sentryShipState,
    setSentryState
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = NHVolumeSavings;
  root.NHVolumeSavings = NHVolumeSavings;

  // MARK:- <ship-filter> sentinel (browser only): judges its parent's [data-restricted-states] candidates on
  // connect (first paint and every Sections-API re-render) and on zone events; hides the parent, heading
  // included, when none show. data-ship-state = Liquid fallback, data-max = visible cap. Uses [hidden]
  // (main.css forces display:none; CarouselSlider skips [hidden] slides), so Liquid's pre-hide and this agree
  if (typeof customElements === 'undefined' || customElements.get('ship-filter')) return;

  // Re-run main.js CarouselSlider.init() so slides, arrows and page count match the visible cards. A carousel
  // whose lazy init has not run yet (no .slides) reads [hidden] itself. Under 2 slides init() bails before
  // the resize listener, so drop the old one (initSlider would read a missing slides[1]) and go inactive
  function refreshCarousel(host) {
    const carousel = host.querySelector('carousel-slider');
    if (!carousel || !carousel.slides || typeof carousel.init !== 'function') return;
    if (carousel.slider) carousel.removeListeners();
    carousel.init();
    if (carousel.slides.length < 2) {
      root.removeEventListener('on:debounced-resize', carousel.breakpointChangeHandler);
      carousel.setAttribute('inactive', '');
    }
  }

  class ShipFilter extends HTMLElement {
    connectedCallback() {
      this.judge();
    }

    judge(detail) {
      const host = this.parentElement;
      if (!host) return;
      const state = sentryState || pickShipState(detail, savedShipState(), this.dataset.shipState);
      const items = Array.from(host.querySelectorAll('[data-restricted-states]'));
      const shown = suggestionsShown(items.map((el) => parseStates(el.dataset.restrictedStates)), state, Number(this.dataset.max) || 0);
      let changed = false;
      items.forEach((el, i) => {
        if (el.hidden === shown[i]) {
          el.hidden = !shown[i];
          changed = true;
        }
      });
      host.hidden = !shown.includes(true);
      if (changed) refreshCarousel(host);
    }
  }
  customElements.define('ship-filter', ShipFilter);
  // MARK:- A zone change is the shopper's new answer, so it drops the Plant Sentry state before the re-judge
  ['zone:updated', 'cart:attribute-updated'].forEach((name) =>
    document.addEventListener(name, (evt) => {
      sentryState = null;
      document.querySelectorAll('ship-filter').forEach((el) => el.judge(evt.detail || {}));
    })
  );
  document.addEventListener('nh:sentry-state', () => {
    document.querySelectorAll('ship-filter').forEach((el) => el.judge());
  });
})(typeof window !== 'undefined' ? window : globalThis);
